// ═══════════════════════════════════════════════════════════
// Standalone Redaction — upload a file, redact it, save it.
// Not tied to any FOIA request. Each staff member sees only
// their own documents.
//
// Routes:
//   /staff/redact            ← this page
//   /staff/redact/:docId     ← PDF editor
//   /staff/redact-csv/:docId ← CSV editor
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  IconUpload, IconLoader2, IconFileTypePdf, IconTable,
  IconAlertCircle, IconDownload, IconLink, IconCheck,
  IconFileDescription, IconTrash,
} from '@tabler/icons-react';
import { uploadAndCreateDocument, getStandaloneDocuments, getStaffDownloadUrl, deleteFoiaDocument, type FoiaDocument } from '../../../api/foiaDocuments';
import { useAuthStore } from '../../../store/authStore';

const ACCEPTED = '.pdf,.csv,text/csv,application/pdf';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fileIcon(mimeType: string) {
  if (mimeType === 'text/csv' || mimeType === 'application/vnd.ms-excel') {
    return <IconTable size={16} style={{ color: 'var(--cds-green-700)', flexShrink: 0 }} />;
  }
  return <IconFileTypePdf size={16} style={{ color: 'var(--cds-red-700)', flexShrink: 0 }} />;
}

function redactRoute(doc: FoiaDocument): string {
  const isCSV = doc.mimeType === 'text/csv' || doc.fileName.toLowerCase().endsWith('.csv');
  return isCSV ? `/staff/redact-csv/${doc.recordId}` : `/staff/redact/${doc.recordId}`;
}

// ── Upload zone ───────────────────────────────────────────

function UploadZone({ onFile }: { onFile: (file: File) => void }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function pick(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    const ok = file.type === 'application/pdf' || file.type === 'text/csv'
      || file.name.toLowerCase().endsWith('.pdf') || file.name.toLowerCase().endsWith('.csv');
    if (!ok) return;
    onFile(file);
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      style={{
        border: `2px dashed ${dragging ? 'var(--color-primary-700)' : 'var(--color-neutral-300)'}`,
        borderRadius: 8,
        background: dragging ? '#F5F4FF' : 'white',
        padding: '40px 24px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <input ref={inputRef} type="file" accept={ACCEPTED} style={{ display: 'none' }} onChange={e => pick(e.target.files)} />
      <IconUpload size={32} style={{ color: 'var(--color-primary-700)', marginBottom: 12 }} />
      <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--color-neutral-800)', marginBottom: 4 }}>
        Drop a file here or click to browse
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-neutral-400)' }}>
        PDF or CSV — max 50 MB
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────

export default function StandaloneRedact() {
  const { user } = useAuthStore();
  const navigate  = useNavigate();

  const [docs,        setDocs]        = useState<FoiaDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  const [uploading,   setUploading]   = useState(false);
  const [uploadPct,   setUploadPct]   = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Per-doc copy-link state: docId → 'idle' | 'copying' | 'copied'
  const [linkState, setLinkState] = useState<Record<number, 'copying' | 'copied'>>({});

  // ── Load library ──────────────────────────────────────

  const loadDocs = useCallback(async () => {
    if (!user?.email) return;
    setLoadingDocs(true);
    try {
      const results = await getStandaloneDocuments(user.email);
      setDocs(results);
    } finally {
      setLoadingDocs(false);
    }
  }, [user?.email]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // ── Upload ────────────────────────────────────────────

  async function handleFile(file: File) {
    if (!user?.email) return;
    setUploading(true);
    setUploadError(null);
    setUploadPct(0);
    try {
      const doc = await uploadAndCreateDocument({
        file,
        source:          'staff',
        uploadedByEmail: user.email,
        notes:           '[STANDALONE]',
        onProgress:      pct => setUploadPct(pct),
      });
      const route = redactRoute(doc);
      navigate(route);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      setUploading(false);
    }
  }

  // ── Copy link ─────────────────────────────────────────

  async function handleCopyLink(doc: FoiaDocument) {
    setLinkState(s => ({ ...s, [doc.recordId]: 'copying' }));
    try {
      const url = await getStaffDownloadUrl(doc);
      if (!url) throw new Error('Could not generate link.');
      await navigator.clipboard.writeText(url);
      setLinkState(s => ({ ...s, [doc.recordId]: 'copied' }));
      setTimeout(() => setLinkState(s => { const n = { ...s }; delete n[doc.recordId]; return n; }), 2500);
    } catch {
      setLinkState(s => { const n = { ...s }; delete n[doc.recordId]; return n; });
    }
  }

  // ── Download ──────────────────────────────────────────

  async function handleDownload(doc: FoiaDocument) {
    const url = await getStaffDownloadUrl(doc);
    if (!url) return;
    const a = document.createElement('a');
    a.href = `/api/s3download?url=${encodeURIComponent(url)}`;
    a.download = doc.redactedStorageUrl
      ? doc.fileName.replace(/(\.\w+)$/, '_redacted$1')
      : doc.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // ── Delete ────────────────────────────────────────────

  async function handleDelete(doc: FoiaDocument) {
    if (!confirm(`Delete "${doc.fileName}"? This cannot be undone.`)) return;
    try {
      await deleteFoiaDocument(doc.recordId);
      setDocs(prev => prev.filter(d => d.recordId !== doc.recordId));
    } catch { /* ignore */ }
  }

  // ── Render ────────────────────────────────────────────

  return (
    <div style={{ padding: '32px 40px', maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 4px' }}>
          Redaction
        </h1>
        <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: 0 }}>
          Upload a PDF or CSV to redact. Your documents are private and only visible to you.
        </p>
      </div>

      {/* Upload zone */}
      <div style={{ marginBottom: 40 }}>
        {uploading ? (
          <div style={{
            border: '2px dashed var(--color-neutral-200)', borderRadius: 8, background: 'white',
            padding: '40px 24px', textAlign: 'center',
          }}>
            <IconLoader2 size={28} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary-700)', marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: 4 }}>
              Uploading… {uploadPct > 0 ? `${uploadPct}%` : ''}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-neutral-400)' }}>Opening editor when ready</div>
          </div>
        ) : (
          <UploadZone onFile={handleFile} />
        )}
        {uploadError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: 'var(--cds-red-700)', background: '#FBEFED', border: '1px solid #F7D5D0', borderRadius: 6, padding: '8px 12px' }}>
            <IconAlertCircle size={14} style={{ flexShrink: 0 }} /> {uploadError}
          </div>
        )}
      </div>

      {/* Library */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--color-neutral-400)', marginBottom: 12 }}>
          My Documents
        </div>

        {loadingDocs ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--color-neutral-400)' }}>
            <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : docs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--color-neutral-400)', fontSize: 14 }}>
            <IconFileDescription size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>No documents yet. Upload a file above to get started.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {docs.map(doc => {
              const ls = linkState[doc.recordId];
              const hasRedacted = !!doc.redactedStorageUrl;
              return (
                <div
                  key={doc.recordId}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: 'white', border: '1px solid var(--color-neutral-100)',
                    borderRadius: 6, padding: '10px 14px',
                  }}
                >
                  {fileIcon(doc.mimeType)}

                  {/* File info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link
                      to={redactRoute(doc)}
                      style={{ fontWeight: 500, fontSize: 14, color: 'var(--color-neutral-800)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {doc.fileName}
                    </Link>
                    <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 1 }}>
                      {formatBytes(doc.fileSize)} · {formatDate(doc.uploadedAt)}
                      {hasRedacted && (
                        <span style={{ marginLeft: 8, color: 'var(--cds-green-700)', fontWeight: 500 }}>· Redacted</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {/* Copy link */}
                    <button
                      title={ls === 'copied' ? 'Copied!' : 'Copy shareable link'}
                      onClick={() => handleCopyLink(doc)}
                      disabled={ls === 'copying'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '5px 10px', borderRadius: 4, border: '1px solid var(--color-neutral-200)',
                        background: ls === 'copied' ? 'var(--cds-green-50)' : 'white',
                        color: ls === 'copied' ? 'var(--cds-green-700)' : 'var(--color-neutral-600)',
                        fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      }}
                    >
                      {ls === 'copying'
                        ? <IconLoader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                        : ls === 'copied'
                          ? <><IconCheck size={13} /> Copied</>
                          : <><IconLink size={13} /> Copy Link</>
                      }
                    </button>

                    {/* Download */}
                    <button
                      title="Download"
                      onClick={() => handleDownload(doc)}
                      style={{
                        display: 'flex', alignItems: 'center', padding: '5px 8px',
                        borderRadius: 4, border: '1px solid var(--color-neutral-200)',
                        background: 'white', color: 'var(--color-neutral-600)',
                        cursor: 'pointer',
                      }}
                    >
                      <IconDownload size={14} />
                    </button>

                    {/* Delete */}
                    <button
                      title="Delete"
                      onClick={() => handleDelete(doc)}
                      style={{
                        display: 'flex', alignItems: 'center', padding: '5px 8px',
                        borderRadius: 4, border: '1px solid var(--color-neutral-200)',
                        background: 'white', color: 'var(--color-neutral-400)',
                        cursor: 'pointer',
                      }}
                    >
                      <IconTrash size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
